import React, { useState, useEffect, useMemo } from "react";
import { FiFile, FiX, FiCheck } from "react-icons/fi";
import UpgradeBanner from "../common/UpgradeBanner";

const FileIcon = FiFile as React.FC<React.SVGProps<SVGSVGElement>>;
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;

interface TemplateData {
  id?: string;
  name: string;
  description: string;
  subject: string;
  content: string;
}

interface CreateTemplateModalProps {
  onClose: () => void;
  onSave: (template: {
    name: string;
    description: string;
    subject: string;
    content: string;
  }) => Promise<void>;
  template?: TemplateData | null; // If provided, we're in edit mode
  isFreePlan?: boolean; // If true, show upgrade banner instead of saving
}

/**
 * CreateTemplateModal - Modal for creating/editing custom email templates
 */
const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  onClose,
  onSave,
  template,
  isFreePlan = false,
}) => {
  const isEditMode = !!template;
  
  const [name, setName] = useState(template?.name || "");
  const [description, setDescription] = useState(template?.description || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [content, setContent] = useState(template?.content || "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Store initial values for change detection in edit mode
  const initialValues = useMemo(() => ({
    name: template?.name || "",
    description: template?.description || "",
    subject: template?.subject || "",
    content: template?.content || "",
  }), [template]);

  // Check if any changes were made
  const hasChanges = useMemo(() => {
    if (!isEditMode) return true; // Always allow save in create mode
    return (
      name !== initialValues.name ||
      description !== initialValues.description ||
      subject !== initialValues.subject ||
      content !== initialValues.content
    );
  }, [isEditMode, name, description, subject, content, initialValues]);

  // Reset form when template changes
  useEffect(() => {
    if (template) {
      setName(template.name || "");
      setDescription(template.description || "");
      setSubject(template.subject || "");
      setContent(template.content || "");
    }
  }, [template]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError("Template name is required");
      return;
    }
    
    if (!subject.trim()) {
      setError("Subject line is required");
      return;
    }

    if (isEditMode && !hasChanges) {
      setError("No changes to save");
      return;
    }

    setIsSaving(true);
    setError(null);
    
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        subject: subject.trim(),
        content: content.trim(),
      });
      
      // Show success message
      setShowSuccess(true);
      
      // Close after showing success briefly
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || `Failed to ${isEditMode ? "update" : "create"} template`);
      setIsSaving(false);
    }
  };

  // Success state
  if (showSuccess) {
    return (
      <div className="su-modal-overlay" onClick={onClose}>
        <div
          className="su-template-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="su-template-success">
            <div className="su-template-success-icon">
              <CheckIcon className="w-8 h-8" />
            </div>
            <h3 className="su-template-success-title">
              {isEditMode ? "Template Updated!" : "Template Created!"}
            </h3>
            <p className="su-template-success-message">
              {isEditMode 
                ? "Your changes have been saved successfully."
                : "Your new template is ready to use."
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="su-modal-overlay" onClick={onClose}>
      <div
        className="su-template-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="su-template-modal-header">
          <div className="su-template-modal-title">
            <FileIcon className="w-5 h-5" />
            <span>{isEditMode ? "EDIT TEMPLATE" : "CREATE TEMPLATE"}</span>
          </div>
          <button
            className="su-template-modal-close"
            onClick={onClose}
            type="button"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="su-template-modal-body">
          {error && (
            <div className="su-template-modal-error">{error}</div>
          )}

          <div className="su-template-field">
            <label className="su-template-label">TEMPLATE NAME</label>
            <input
              type="text"
              className="su-template-input"
              placeholder="e.g., Weekly Team Update"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="su-template-field">
            <label className="su-template-label">DESCRIPTION</label>
            <input
              type="text"
              className="su-template-input"
              placeholder="Brief description of when to use this template"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="su-template-field">
            <label className="su-template-label">SUBJECT LINE</label>
            <input
              type="text"
              className="su-template-input"
              placeholder="Email subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="su-template-field">
            <label className="su-template-label">EMAIL CONTENT</label>
            <textarea
              className="su-template-textarea"
              placeholder="Write your template content here. Use [brackets] for placeholder values..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
            />
          </div>

          {/* Upgrade Banner for Free Users */}
          {isFreePlan && !isEditMode && (
            <div className="su-template-upgrade-banner">
              <UpgradeBanner
                title="Upgrade to Save Custom Templates"
                subtitle="Create and save your own reusable email templates with a paid plan"
                buttonText="UPGRADE NOW"
              />
            </div>
          )}

          {/* Footer */}
          <div className="su-template-modal-footer">
            <button
              type="submit"
              className={`su-template-btn-primary ${(isEditMode && !hasChanges) || (isFreePlan && !isEditMode) ? "su-template-btn-disabled" : ""}`}
              disabled={isSaving || (isEditMode && !hasChanges) || (isFreePlan && !isEditMode)}
            >
              {isSaving
                ? "SAVING..."
                : isEditMode
                  ? "SAVE CHANGES"
                  : "CREATE TEMPLATE"
              }
            </button>
            <button
              type="button"
              className="su-template-btn-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              CANCEL
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
